import { createFileRoute, Link } from '@tanstack/react-router'
import { Plus, Search, LayoutGrid, List, Users } from 'lucide-react'
import { useState } from 'react'

import { TeamForm } from '@/components/pm/team/team-form'
import { EmptyState } from '@/components/ui/empty-state'
import {
  ResponsiveListContainer,
  ListHeader,
  FilterBar,
  SearchBox,
  ResponsiveGrid,
  ResponsiveTable,
} from '@/components/ui/responsive-list'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CardGridSkeleton, TableRowsSkeleton } from '@/components/ui/skeleton'
import { useDebouncedValue } from '@/hooks'
import { useTeams, type TeamStatus, type TeamListParams } from '@/hooks'
import { cn } from '@/lib/utils'

/**
 * 团队列表页面
 */
export const Route = createFileRoute('/_authenticated/pm/teams/')({
  component: TeamListPage,
})

const STATUS_LABELS: Record<TeamStatus, string> = {
  active: '活跃',
  archived: '已归档',
}

const STATUS_COLORS: Record<TeamStatus, string> = {
  active: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-800',
}

function TeamListPage() {
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card')
  const [filters, setFilters] = useState<TeamListParams>({
    page: 1,
    pageSize: 20,
  })
  const [searchInput, setSearchInput] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)

  // 使用防抖搜索
  const debouncedSearch = useDebouncedValue(searchInput, 300)

  const { data: teams, isLoading, error } = useTeams({
    ...filters,
    search: debouncedSearch || undefined,
  })

  const handleStatusFilter = (status: TeamStatus | undefined) => {
    setFilters((prev) => ({ ...prev, status, page: 1 }))
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  return (
    <ResponsiveListContainer>
      {/* 页面标题 */}
      <ListHeader
        title="团队管理"
        description="管理团队和团队成员"
        actions={
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">新建团队</span>
            <span className="sm:hidden">新建</span>
          </button>
        }
      />

      {/* 新建团队表单 */}
      {showCreateForm && (
        <TeamForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => setShowCreateForm(false)}
        />
      )}

      {/* 筛选和搜索 */}
      <FilterBar>
        {/* 搜索框 */}
        <SearchBox>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="搜索团队..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </SearchBox>

        {/* 状态筛选 - 移动端使用下拉框 */}
        <div className="flex items-center gap-2 sm:hidden">
          <Select
            value={filters.status ?? 'all'}
            onValueChange={(value) => handleStatusFilter(value === 'all' ? undefined : value as TeamStatus)}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="全部状态" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部状态</SelectItem>
              {(Object.keys(STATUS_LABELS) as TeamStatus[]).map((status) => (
                <SelectItem key={status} value={status}>{STATUS_LABELS[status]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 状态筛选 - 桌面端使用按钮组 */}
        <div className="hidden sm:flex items-center gap-2">
          <button
            onClick={() => handleStatusFilter(undefined)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              !filters.status
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            全部
          </button>
          {(Object.keys(STATUS_LABELS) as TeamStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => handleStatusFilter(status)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                filters.status === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              {STATUS_LABELS[status]}
            </button>
          ))}
        </div>

        {/* 视图切换 */}
        <div className="flex items-center gap-1 rounded-md border p-1 ml-auto">
          <button
            onClick={() => setViewMode('card')}
            className={cn(
              'rounded p-1.5 transition-colors',
              viewMode === 'card' ? 'bg-muted' : 'hover:bg-muted/50'
            )}
            title="卡片视图"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={cn(
              'rounded p-1.5 transition-colors',
              viewMode === 'list' ? 'bg-muted' : 'hover:bg-muted/50'
            )}
            title="列表视图"
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </FilterBar>

      {/* 团队列表 */}
      {isLoading ? (
        viewMode === 'card' ? (
          <CardGridSkeleton count={8} />
        ) : (
          <TableRowsSkeleton rows={5} columns={4} />
        )
      ) : !teams || teams.length === 0 ? (
        <EmptyState
          icon={Users}
          title="暂无团队"
          description="点击上方按钮创建第一个团队"
          action={{
            label: '新建团队',
            onClick: () => setShowCreateForm(true),
          }}
        />
      ) : viewMode === 'card' ? (
        <ResponsiveGrid columns={4}>
          {teams.map((team) => (
            <Link
              key={team.id}
              to="/pm/teams/$teamId"
              params={{ teamId: team.id }}
              className="group rounded-lg border bg-card p-4 transition-shadow hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="truncate font-semibold group-hover:text-primary">
                    {team.name}
                  </h3>
                </div>
                <span
                  className={cn(
                    'ml-2 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                    STATUS_COLORS[team.status]
                  )}
                >
                  {STATUS_LABELS[team.status]}
                </span>
              </div>
              {team.description && (
                <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                  {team.description}
                </p>
              )}
              <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <Users className="h-3.5 w-3.5" />
                <span>创建于 {new Date(team.createdAt).toLocaleDateString('zh-CN')}</span>
              </div>
            </Link>
          ))}
        </ResponsiveGrid>
      ) : (
        <ResponsiveTable>
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium">团队名称</th>
                <th className="px-4 py-3 text-left text-sm font-medium hidden sm:table-cell">描述</th>
                <th className="px-4 py-3 text-left text-sm font-medium">状态</th>
                <th className="px-4 py-3 text-left text-sm font-medium hidden md:table-cell">创建时间</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => (
                <tr key={team.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <Link
                      to="/pm/teams/$teamId"
                      params={{ teamId: team.id }}
                      className="font-medium hover:text-primary hover:underline"
                    >
                      {team.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground hidden sm:table-cell">
                    {team.description || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        STATUS_COLORS[team.status]
                      )}
                    >
                      {STATUS_LABELS[team.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">
                    {new Date(team.createdAt).toLocaleDateString('zh-CN')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ResponsiveTable>
      )}
    </ResponsiveListContainer>
  )
}
